import {
  Module,
  VuexModule,
  Mutation,
  getModule,
} from "vuex-module-decorators";

const name = "theme";

import store from "@/store";
// Imported from an import-free leaf: resolveInitialDark runs from a property initializer during
// bootstrap, where a throw blanks the whole app. See the note in PersistedState.ts.
import { isBoolean, readPersistedJson } from "@/store/PersistedState";

if (store.state[name]) {
  store.unregisterModule(name);
}

/**
 * Resolves the initial dark-mode preference.
 *
 * Precedence: a value the user previously chose (persisted in localStorage under
 * the `"theme"` key) wins; otherwise fall back to the operating-system setting via
 * `prefers-color-scheme`. The `matchMedia` guard keeps this safe under jsdom/unit
 * tests where `window.matchMedia` is not implemented.
 */
function resolveInitialDark(): boolean {
  const stored = readPersistedJson(name, isBoolean);
  if (stored !== null) {
    return stored;
  }
  return typeof window !== "undefined" && !!window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;
}

/**
 * Persists the user's light/dark appearance preference per-browser (localStorage),
 * mirroring {@link LanguageVuexModule}. The Vuetify runtime theme is initialised from
 * this module in `@/plugins/vuetify` and toggled at runtime via `changeThemeTo`.
 */
@Module({ dynamic: true, namespaced: true, store, name: name })
export class ThemeVuexModule extends VuexModule {
  isDark = resolveInitialDark();

  /** Sets and persists the dark-mode preference. */
  @Mutation
  setDark(isDark: boolean) {
    this.isDark = isDark;
    localStorage.setItem(name, JSON.stringify(isDark));
  }

  /** Clears the stored preference, re-deriving the default from the OS setting. */
  @Mutation
  clear() {
    localStorage.removeItem(name);
    this.isDark = resolveInitialDark();
  }
}

export const ThemeModule = getModule(ThemeVuexModule);
